import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The course a teacher is working in. The students list, the metrics and the Q&A console all work
 * on one course at a time; picking it again on every page (each used to open on the first course)
 * threw the teacher's context away. The choice lives in `?courseId=` (so a link or a refresh lands
 * on it, as the student page already does) and in localStorage (so the next teacher page opens on
 * the same course).
 *
 *   const [courseId, selectCourse] = useTeacherCourse(courses);
 *
 * `courses` is the teacher's course list; until it has loaded the id is null. An id that is not in
 * the list (a course the teacher no longer has) is ignored, never trusted. Order of preference:
 * `?courseId=`, `options.preferred` (a route parameter), the last course the teacher picked, the
 * first course in the list.
 */

const STORAGE_KEY = 'crm-teacher-course';

function readStored() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function writeStored(id) {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* storage unavailable (private window): the URL still carries the choice */
  }
}

export function useTeacherCourse(courses, options = {}) {
  const { preferred = '' } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const [stored, setStored] = useState(readStored);

  const list = Array.isArray(courses) ? courses : [];
  const known = (id) => Boolean(id) && list.some((course) => course.id === id);
  const courseId = [searchParams.get('courseId'), preferred, stored].find(known)
    || (list.length > 0 ? list[0].id : null);

  // What is on screen is what the next page opens on.
  useEffect(() => {
    if (courseId && courseId !== stored) {
      writeStored(courseId);
      setStored(courseId);
    }
  }, [courseId, stored]);

  const selectCourse = useCallback((id) => {
    if (!id) return;
    writeStored(id);
    setStored(id);
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('courseId', id);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return [courseId, selectCourse];
}

export default useTeacherCourse;
